import { useMemo } from 'react';
import { EmptyState } from '../../../components/ui';
import Box from './Box';
import MiniStat from './MiniStat';
import Badge from '../../../components/ui/Badge';
import { useStudentEngagement, useStudentWellbeing } from '../../../api/hooks';
import { isWellbeingRaw } from '../../../api/types';
import { humanize } from '../../../lib/formatLabels';
import { parseDate, weekRange } from '../lib/format';
import { FaFileAlt, FaCheck, FaExclamationTriangle, FaArrowRight } from '../../../components/ui/icons';
import type { WeeklySummary, SummaryBulletTone } from '../../../api/types';

interface Props {
  summary: WeeklySummary | null;
  /** When given, the summary also shows the week's study time and check-ins (artboard `.mini3`). */
  studentId?: number;
}

const TONE_META: Record<SummaryBulletTone, {
  tone: 'success' | 'warning' | 'primary';
  label: string;
  icon: React.ReactNode;
}> = {
  strength:   { tone: 'success', label: 'STRENGTH',   icon: <FaCheck /> },
  concern:    { tone: 'warning', label: 'CONCERN',    icon: <FaExclamationTriangle /> },
  suggestion: { tone: 'primary', label: 'SUGGESTION', icon: <FaArrowRight /> },
};

/** Artboard `.mini3`: this week's study time against the usual level, and the week's check-ins. */
function WeekTiles({ studentId, weekOf }: { studentId: number; weekOf: string | null }) {
  const eng = useStudentEngagement(studentId);
  const wb = useStudentWellbeing(studentId);
  const checkins = useMemo(() => {
    if (!wb.data || !isWellbeingRaw(wb.data)) return null;
    const start = parseDate(weekOf);
    if (!start) return null;
    const from = start.getTime();
    const to = from + 7 * 86_400_000;
    return wb.data.checkin_history
      .filter((e) => { const t = new Date(e.created_at).getTime(); return t >= from && t < to; })
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [wb.data, weekOf]);
  const series = eng.data?.daily_minutes_series ?? [];
  const withBase = series.filter((r) => r.personal_baseline !== null);
  const baseline = withBase.length ? withBase[withBase.length - 1].personal_baseline : null;
  const usual = baseline !== null && baseline > 0 ? Math.round(baseline * 7) : null;
  const minutes = eng.data?.kpis.total_study_minutes_7d ?? null;
  if (minutes === null && checkins === null) return null;
  const lastState = checkins && checkins.length > 0
    ? String((checkins[0].payload as any)?.answer_slug ?? (checkins[0].payload as any)?.state ?? '')
    : '';
  return (
    <div className="mt-3.5 grid grid-cols-[repeat(auto-fit,minmax(0,1fr))] gap-2.5">
      {minutes !== null && (
        <MiniStat label="Study time" value={`${minutes} min`} tone={usual !== null && minutes < usual ? 'down' : 'default'}
          sub={usual !== null ? `usual ${usual}` : undefined} />
      )}
      {checkins !== null && (
        <MiniStat label="Check-ins" value={checkins.length} sub={lastState ? humanize(lastState).toLowerCase() : 'this week'} />
      )}
    </div>
  );
}

export default function WeeklySummaryPanel({ summary, studentId }: Props) {
  if (summary === null) {
    return (
      <Box title="This week" help="weekly_summary">
        <EmptyState compact
          icon={<FaFileAlt />}
          title="No summary this week"
          body="Activity hasn't generated enough signal yet, or the nightly summary hasn't run." />
      </Box>
    );
  }

  return (
    <Box title="This week" help="weekly_summary" actions={
      summary.week_of ? <span className="font-mono text-xs font-medium text-muted">{weekRange(summary.week_of)}</span> : undefined}>
      <p className="mb-4 font-display text-[17px] font-bold leading-snug text-ink">
        {summary.headline}
      </p>
      <ul className="space-y-2">
        {summary.bullets.map((b, idx) => {
          const meta = TONE_META[b.tone];
          return (
            <li key={idx} className="grid grid-cols-[104px_minmax(0,1fr)] items-start gap-3 text-sm">
              <div className="pt-0.5">
                <Badge tone={meta.tone} size="sm">
                  <span className="mr-1 inline-flex items-center">{meta.icon}</span>
                  {meta.label}
                </Badge>
              </div>
              <span className="text-ink">{b.text}</span>
            </li>
          );
        })}
      </ul>
      {studentId != null && <WeekTiles studentId={studentId} weekOf={summary.week_of} />}
    </Box>
  );
}
